import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock, Tv } from "lucide-react";
import api, { fmtTimeCT } from "@/lib/api";

function todayCT() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" }); // YYYY-MM-DD
}
function dateCT(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
  } catch {
    return "";
  }
}
function dayCT(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { timeZone: "America/Chicago", weekday: "short" }).toUpperCase();
  } catch {
    return "";
  }
}

function GameChip({ g, i, prefix, when }) {
  const e = g.event;
  const t = g.team;
  return (
    <Link
      key={`${t.slug}-${e.id}-${i}`}
      to={`/team/${t.slug}`}
      data-testid={`${prefix}-${t.slug}-${i}`}
      className="group flex items-center gap-3 rounded-lg border border-bline bg-ink px-3 py-2.5 transition-colors duration-200 hover:border-volt/50"
    >
      <span className="flex -space-x-2">
        {[e.away, e.home].map((side, j) =>
          side?.logo ? (
            <img key={j} src={side.logo} alt="" className="h-8 w-8 rounded-full border border-bline bg-surface object-contain p-0.5" />
          ) : (
            <span key={j} className="flex h-8 w-8 items-center justify-center rounded-full border border-bline bg-surface font-score text-[9px] text-slate-400">
              {side?.abbr || "?"}
            </span>
          )
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-white group-hover:text-volt">{e.name}</span>
        <span className="flex items-center gap-2 font-score text-[9px] tracking-widest text-slate-500">
          {t.league}
          {e.broadcast && (
            <span className="inline-flex items-center gap-1">
              <Tv className="h-2.5 w-2.5 text-bcast" /> {e.broadcast}
            </span>
          )}
        </span>
      </span>
      <span className="shrink-0 rounded-md bg-volt/10 px-2 py-1 font-score text-xs font-bold tracking-wider text-volt">
        {when}
      </span>
    </Link>
  );
}

export default function TodaysGames() {
  const [games, setGames] = useState(null);
  const [nextGames, setNextGames] = useState([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/calendar");
        if (!mounted) return;
        const today = todayCT();
        const now = new Date();
        const upcoming = (list) =>
          (list || [])
            .filter((g) => g.event?.status?.state === "pre" && new Date(g.event.date) > now)
            .sort((a, b) => new Date(a.event.date) - new Date(b.event.date));
        const todays = upcoming(data.games).filter((g) => dateCT(g.event.date) === today);
        if (todays.length) {
          setGames(todays);
          setNextGames([]);
          return;
        }
        // nothing today — surface the next available games (this week, then next)
        let next = upcoming(data.games);
        if (!next.length) {
          const { data: w2 } = await api.get("/calendar?week_offset=1");
          if (!mounted) return;
          next = upcoming(w2.games);
        }
        setGames([]);
        setNextGames(next.slice(0, 6));
      } catch {
        if (mounted) setGames([]);
      }
    };
    load();
    const t = setInterval(load, 60000); // games drop off the moment they start
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  if (!games) return null;

  if (games.length === 0) {
    return (
      <section className="border-b border-bline bg-surface/40" data-testid="todays-games">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-8 lg:px-12">
          <div className="mb-3 flex items-center gap-3">
            <CalendarDays className="h-4 w-4 text-slate-500" />
            <p className="font-score text-xs tracking-[0.3em] text-slate-400" data-testid="todays-games-empty">NO SCHEDULED GAMES TODAY</p>
            {nextGames.length > 0 && (
              <span className="font-score text-[10px] tracking-widest text-slate-600">NEXT UP ACROSS THE METROPLEX</span>
            )}
          </div>
          {nextGames.length > 0 ? (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="next-games">
              {nextGames.map((g, i) => (
                <GameChip key={`${g.team.slug}-${g.event.id}-${i}`} g={g} i={i} prefix="next-game" when={`${dayCT(g.event.date)} ${fmtTimeCT(g.event.date)}`} />
              ))}
            </div>
          ) : (
            <Link to="/calendar" data-testid="next-games-calendar-link" className="inline-flex items-center gap-2 font-score text-[10px] tracking-widest text-volt hover:underline">
              BROWSE THE FULL CALENDAR →
            </Link>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="border-b border-bline bg-surface/40" data-testid="todays-games">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-8 lg:px-12">
        <div className="mb-3 flex items-center gap-3">
          <Clock className="h-4 w-4 text-volt" />
          <p className="font-score text-xs tracking-[0.3em] text-volt">TODAY'S GAMES</p>
          <span className="font-score text-[10px] tracking-widest text-slate-600">
            {games.length} {games.length === 1 ? "GAME" : "GAMES"} BEFORE FIRST PITCH · TIP · KICKOFF
          </span>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {games.map((g, i) => (
            <GameChip key={`${g.team.slug}-${g.event.id}-${i}`} g={g} i={i} prefix="todays-game" when={fmtTimeCT(g.event.date)} />
          ))}
        </div>
      </div>
    </section>
  );
}
